import { RSB_URL } from 'helpers/common/rsb';

export const lessons: LessonsSetup = {
    '1.1': {
        title: 'Welcome!',
        image: '/assets/illustrations/ideas.png',
        imageAlt: 'Pile of lightbulbs',
        text: `<p>This simple innovation game explains how funding decisions can impact {metricName}.</p>
                <p>You'll play the new VP of Innovation responsible for setting up an innovation program.</p>
                <p>Each ball you play represents a new idea running as an innovation project.</p>
                <p><b>Get started by kicking off a few ideas and see what happens!</b></p>`,
    },
    '1.2': {
        title: 'STOP!',
        image: '/assets/illustrations/stop-sign.png',
        imageAlt: 'Stop sign',
        text: `<p>First, congrats on your appointment as VP of Innovation.</p>
                <p>It's clear that you're excited to get moving. But you can't just spend money like that!</p>
                <p>Let's go over the rules...</p>`,
        button1: {
            label: 'Tell me the rules',
            lesson: '1.3',
        },
        button2: {
            color: 'secondary',
            label: "I know how this works - let's go!",
            lesson: '1.6',
        },
    },
    '1.3': {
        title: 'Ideas = Projects',
        text: `<p>Each idea represents a team working on a new innovation project.</p>
                <p>They could be working on anything - a blockchain application, a clothing line, or a delicious frozen snack.</p>
                <p>You are <u>not</u> in charge of coming up with ideas. You can't control that.</p>
                <p>You're the investor. You allocate the money.</p>`,
        button1: {
            lesson: '1.4',
            label: 'How do I win?',
        },
        annotation1: 'These are your innovation projects.',
    },
    '1.4': {
        title: 'Business Outcomes',
        text: `<p>Ideas moving down the board are making progress towards the market, and will result in a business outcome.</p>
                <p>That outcome could be revenue, social impact, sustainability target, or other intangible. But in this game we will be simulating revenue.</p>
                <p>When the idea reaches the market, you get to see how much money you earned with each idea.</p>`,
        button1: {
            label: 'Is this free money?',
            lesson: '1.5',
        },
        annotation1: 'This is your total outcome.',
        annotation2: 'These are your possible outcomes for each project.',
    },
    '1.5': {
        title: 'Costs',
        image: '/assets/illustrations/kro-ceo-cash.png',
        imageAlt: 'CEO giving budget',
        text: `<p>Nothing is free. The CEO has allocated an innovation budget of {budget}.</p>
                <p>As an idea moves down the board and towards the market, it costs you money with each step.</p>
                <p>The project teams spend this money to get more information about the market. As the ideas get closer to the market, you get more and more information about the likely business outcomes.</p>`,
        button1: {
            label: "Great, let's play",
            lesson: '1.6',
        },
        annotation1: 'This is your budget.',
        annotation2:
            "The costs are for running experiments in sprints. You pay for more information about the project's likelihood of success.",
    },
    '1.6': {
        title: 'Predict the {metricName}',
        image: '/assets/illustrations/magician.png',
        imageAlt: 'Wizard with crystal ball',
        text: `<p>The CEO and CFO both want to know what your <i>{metricFullName} ({metricName})</i> will be before they'll hand over the budget.</p>
                <p>What will you tell them?</p>`,
        button1: {
            label: '{metricGoalLow} {metricName}',
            lesson: '1.7',
        },
        button2: {
            label: '{metricGoalMid} {metricName}',
            lesson: '1.7',
        },
        button3: {
            label: '{metricGoal} {metricName}',
            lesson: '1.9',
        },
        button4: {
            label: 'I have no idea',
            lesson: '1.7',
        },
        annotation1: 'The {metricName} is here.',
    },
    '1.7': {
        title: 'Aim Higher!',
        image: '/assets/illustrations/cfo-unhappy.png',
        imageAlt: 'CFO with thumbs down',
        sound: 'negative',
        text: `<p>The CFO says they won't fund your innovation program unless the <i>{metricName}</i> is {metricGoal} minimum.</p>
                <p>Well, I guess we'll have to tell them what they want to hear.</p>`,
        button1: {
            label: 'I agree to {metricGoal} and surrender to the corporate machine',
            lesson: '1.9',
        },
        button2: {
            color: 'secondary',
            label: "I'll just quit and find a better job",
            lesson: '1.8',
        },
    },
    '1.8': {
        title: 'Congrats!',
        image: '/assets/illustrations/money-bag.png',
        imageAlt: 'Bag of money',
        text: `<p>First, congrats on quitting and finding a new VP of Innovation job with a much more reasonable boss.</p>
                <p>The CEO has given you a budget of {budget} and it will cost {costsTotal} to get an idea to the market and see what the returns are.</p>`,
        button1: {
            label: 'Got it - free will is overrated anyway',
            lesson: '1.6',
        },
    },
    '1.9': {
        title: 'Budget Approved',
        image: '/assets/illustrations/cfo-happy.png',
        imageAlt: 'CFO with thumbs up',
        sound: 'positive',
        text: `<p>The CFO is now eagerly awaiting the {metricGoal} return you've promised.</p>
                <p>Let's hope you get at least a couple of winners on the right side of the board!</p>
                <p><b>Kick off five ideas!</b></p>`,
    },
    '1.10': {
        title: 'Keep Going',
        text: `<p>Keep track of your budget and money earned.</p>
                <p>You have enough money for {kickoffsRemaining} more {tries}.</p>
                <p><b>{action}</b></p>`,
    },
    '1.11': {
        title: "Ouch... That Didn't Work",
        image: '/assets/illustrations/empty-pockets.png',
        imageAlt: 'Empty pocket',
        text: `<p>You're not going to get a promotion losing company money, but innovation is pretty random. It takes a lot of tries to get a billion dollar idea.</p>
                <p>Don't feel bad, the <i>Expected {metricName}</i> of this board is {metricMaxDefault}. Plenty of companies never find the next big thing.</p>
                <p>But there are some things you can try...</p>`,
        button1: {
            label: 'Is this how it works in the real world?',
            lesson: '1.12',
        },
        button2: {
            color: 'secondary',
            label: 'Restart and try again',
            lesson: '1.9',
        },
    },
    '1.12': {
        title: 'The Real World',
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Globe',
        sound: 'lesson-learned',
        text: `<p>In the real world, 50% of business ideas fail, 40% barely survive, 9% are mediocre, and only 1% of startups achieve great success. You might get lucky with even one idea, but the odds are against you.</p>        
                <ul>
                    <li>A small budget = few chances of success.</li>
                    <li>It takes a lot of ideas to consistently show success.</li>
                </ul>
                <p>The best early-stage investors know it's impossible to say which projects will succeed and which will fail. So they take a different approach...</p>`,
        button1: {
            label: 'How do smart investors work?',
            lesson: '2',
        },
        annotation1: 'This is your budget.',
        annotation2: 'This is your total result.',
        annotation3:
            "The costs are for running experiments in sprints. You pay for more information about the project's likelihood of success.",
        annotation4: 'These are your outcomes.',
    },
    '2': {
        heading: 'Level 2',
        title: 'Data-Driven Decisions',
        text: 'Pause ideas with a stage gate and decide to continue investing or not.',
        button: 'Play',
        image: '/assets/illustrations/datadriven-decisions.png',
        imageAlt: 'A funnel with ideas coming in and money going out',
    },
    '2.1': {
        title: 'Data-Driven Decisions',
        image: '/assets/illustrations/datadriven-decisions.png',
        imageAlt: 'A funnel with ideas coming in and money going out',
        text: `<p>The CEO is benevolent and has given you another chance.</p>
                <p>This time, instead of investing a full {costsTotal} in each idea, stop halfway and evaluate if you should keep investing.</p>`,
        button1: {
            label: "What's my budget?",
            lesson: '2.1.1',
        },
        annotation1: 'This is a go / no go stage of product or business development.',
    },
    '2.1.1': {
        title: 'Better Budget',
        image: '/assets/illustrations/kro-ceo-money2.png',
        imageAlt: 'CEO giving budget',
        text: `<p>The CEO is feeling generous and has given you a better budget of {budget}.</p>
                <p>Now you can fund ten full projects.</p>
                <p><b>Kick off ten ideas!</b></p>`,
    },
    '2.2': {
        title: 'Evaluating Data',
        image: '/assets/illustrations/evaluate.png',
        imageAlt: 'Magnifying glass',
        text: `<p>You spent half your money with no <i>{metricName}</i>, but you've generated data. The team is getting market feedback and reporting progress left or right.</p>
               <p>If an idea looks promising, then invest the remaining {remaining} needed to take the idea all the way to market launch.</p>`,
        button1: {
            label: "Ok - let's make some decisions",
            lesson: '2.3',
        },
    },
    '2.3': {
        title: 'First Gate',
        text: `<p>Decide if you want to invest more in the ideas at the left-most gate.</p>
                <p>Roll over the first gate to see a hint.</p>
                <p><b><i>If you decide to open it</i>, click on the gate.</b></p>`,
        button1: {
            color: 'secondary',
            label: 'No, keep that one closed',
            lesson: '2.5',
        },
    },
    '2.4': {
        title: 'Oops!',
        image: '/assets/illustrations/bad-idea.png',
        imageAlt: 'Broken lightbulb',
        sound: 'negative',
        text: `<p>That wasn't great. The odds of that gate delivering <i>{metricName}</i> were not good.</p>
                <p>With luck, you can win with a bad decision, but luck won't help you win in the long run. Better to stop ideas that look likely to fail.</p>`,
        button1: {
            action: 'reset',
            label: 'Let me restart and try again',
        },
    },
    '2.5': {
        title: 'Good Decision! ',
        sound: 'positive',
        text: `<p>There are no good outcomes from that gate. At best, you'd break even.</p>
                <p>What about the second gate?</p>
                <p><b><i>If you decide to open it</i>, click on the gate.</b></p>`,
        button1: {
            color: 'secondary',
            label: 'No, keep it closed',
            lesson: '2.6',
        },
    },
    '2.6': {
        title: 'Good Call!',
        sound: 'positive',
        text: `<p>It's possible to get maximum of {maximum} return from the second gate, but it's far more likely to get nothing.</p>
                <p>What about the third gate?</p>
                <p><b><i>If you decide to open it</i>, click on the gate.</b></p>`,
        button1: {
            color: 'secondary',
            label: 'No, keep it closed',
            lesson: '2.7',
        },
    },
    '2.7': {
        title: 'Excellent Decision! ',
        sound: 'positive',
        text: `<p>It looks like you can get {maximum} from the third gate, but it's more likely you get nothing or just break even.</p>
                <p>What about the fourth gate?</p>
                <p><b><i>If you decide to open it</i>, click on the gate.</b></p>`,
        button1: {
            color: 'secondary',
            label: 'No, keep it closed',
            lesson: '2.8',
        },
    },
    '2.8': {
        title: 'Too Cautious?',
        image: '/assets/illustrations/mag-glass.png',
        imageAlt: 'Magnifying glass',
        sound: 'negative',
        text: `<p>There are six possible results from the fourth gate. Three of them earn you a payout. One is break even. And only two lose money.</p>
                <p>This gate is a good bet and <b>you should click on the gate to open it.</b></p>`,
    },
    '2.9': {
        title: 'Great Decision!',
        sound: 'positive',
        text: `<p>Even if the ideas at that gate didn't pay out, it was the best decision you could make based on the data.</p>
                <p>You'll win some and lose some, but in the long run you'll win.</p>
                <p>What about the fifth gate?</p>
                <p><b><i>If you decide to open it</i>, click on the gate.</b></p>`,
        button1: {
            color: 'secondary',
            label: 'No, keep it closed',
            lesson: '2.10',
        },
    },
    '2.10': {
        title: 'Huh?',
        image: '/assets/illustrations/mag-glass.png',
        imageAlt: 'Magnifying glass',
        sound: 'negative',
        text: `<p>The fifth gate is almost a sure thing!</p>
                <p>Four out of six outcomes are positive, one breaks even, and only one loses.</p>
                <p><b>You should click on the fifth gate to open it.</b></p>`,
    },
    '2.11': {
        title: 'Easy Decision!',
        sound: 'positive',
        text: `<p>The odds here still look good.</p>
                <p>What about the last gate?</p>
                <p><b><i>If you decide to open it</i>, click on the gate.</b></p>`,
        button1: {
            color: 'secondary',
            label: 'No, keep it closed',
            lesson: '2.12',
        },
    },
    '2.12': {
        title: 'Hmm...',
        image: '/assets/illustrations/mag-glass.png',
        imageAlt: 'Magnifying glass',
        sound: 'negative',
        text: `<p>This one is a sure thing.</p>
                <p>Unless you hate winning. <b>You should click on the gate to open it.</b></p>`,
    },
    '2.13': {
        title: 'Great Decisions!',
        image: '/assets/illustrations/ideas.png',
        imageAlt: 'Pile of lightbulbs',
        sound: 'positive',
        text: `<p>By stopping ideas that looked likely to fail, you've saved some budget and increased your returns!</p>
                <p>You saved {saved} total by stopping {stopped} ideas. So why not invest that money in some new projects?</p>
                <p><b>Kick off new ideas and spend the rest of your budget.</b></p>`,
    },
    '2.14': {
        title: 'Fantastic!',
        image: '/assets/illustrations/victory.png',
        imageAlt: 'People celebrating',
        text: `<p>You reinvested money by stopping failed projects and earned even more <i>{metricName}</i> than you could have otherwise.</p>
                <p>You returned {metricValue}. It's not the {metricGoal} you promised but you're making progress and you've still got a job.</p>
                <p>You've opened and closed gates to create <i>decision criteria</i> for reinvesting in a project. Any project that isn't going in the right direction gets stopped.</p>`,
        button1: {
            label: 'How does this work in the real world?',
            lesson: '2.15',
        },
    },
    '2.15': {
        title: 'The Real World',
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Globe',
        sound: 'lesson-learned',
        text: `<p>Real innovation project teams run experiments to show evidence of market interest to investors. Smart investors then double down on projects showing valid data.</p>
                <p>Bad investors who make decisions with their gut can get lucky, but usually don't last long.</p>
                <ul>
                    <li>Making decisions based on data increases <i>{metricName}</i>.</li>
                    <li>Create a consistent decision criteria.</li>
                </ul>`,
        button1: {
            label: 'What is <i>Expected {metricName}</i>?',
            lesson: '3',
        },
        annotation1: "You've improved your {metricName}.",
        annotation2: 'This is a go / no go stage of product or business development.',
    },
    '3': {
        heading: 'Level 3',
        title: 'Predicting the Future',
        text: 'Calculate your <i>Expected {metricName}</i> based on your decision criteria.',
        button: 'Play',
        image: '/assets/illustrations/crystal-ball.png',
        imageAlt: 'Crystal ball',
    },
    '3.1': {
        title: 'Predicting the Future',
        image: '/assets/illustrations/crystal-ball.png',
        imageAlt: 'Crystal ball',
        text: `<p>Before you can get to <i>{metricGoal}</i> {metricName}, you need to know the difference between <i>{metricName}</i> and <i>Expected {metricName}</i>.</p>
                <p>Your last <i>{metricName}</i> was {prevMetricValue} based on your ten attempts with a budget of {budget}.</p>
                <p>Let's see if you can get the same result again.</p>
                <p><b>Launch ten new ideas!</b></p>`,
    },
    '3.2': {
        title: 'Random Outcomes',
        text: `<p>Last time, you returned {prevMetricValue}. This time you returned {metricValue}, even though you had the same gates open.</p>
                <p>Obviously there is some randomness here. That's called <i>variance</i>. But we need a consistent way to predict our outcome.</p>
                <p>We can calculate the <i>Expected {metricName}</i> to make the best possible decision criteria.</p>`,
        button1: {
            label: 'How do you calculate <i>Expected {metricName}</i>?',
            lesson: '3.3',
        },
        button2: {
            color: 'secondary',
            label: 'I know this already, so please skip this lesson',
            lesson: '3.13',
        },
    },
    '3.3': {
        title: 'Simple Decisions',
        text: `<p>Let's start simple.</p>
                <p>Here, we have an idea stopped at the bottom gate.</p>
                <p>It can either drop to the right, returning <b>{impactRight}</b>, or to the left, returning <b>{impactLeft}</b>.</p>
                <p>We can actually calculate the value of the idea at the gate.</p>`,
        button1: {
            label: 'Ok... show me',
            lesson: '3.4',
        },
        button2: {
            color: 'secondary',
            label: 'Wait... are you trying to teach me math?',
            lesson: '3.3.1',
        },
    },
    '3.3.1': {
        title: 'Simple Math Only',
        text: `<p>Yes, but don't panic.</p>
                <p>We're only using addition and multiplication here. We'll do all the number crunching for you and there is no quiz at the end.</p>
                <p>Just follow along and we'll explain step by step.</p>`,
        button1: {
            label: "Ok... I'm trusting you",
            lesson: '3.4',
        },
    },
    '3.4': {
        title: 'Right Outcome',
        text: `<p>There's a <b>50%</b> chance of the idea going to the right and returning <b>{impactRight}</b>.</p>
                <p>We can multiply the chance of an event by the outcome to get the expected value.</p>
                <p>In this case, that's <b>50% &times; {impactRight} = <gold>{expectedImpactRight}</gold></b>.</p>`,
        button1: {
            label: 'What about the chance of going left?',
            lesson: '3.5',
        },
    },
    '3.5': {
        title: 'Left Outcome',
        text: `<p>There's also a <b>50%</b> chance of the idea going to the left and returning <b>{impactLeft}</b>.</p>
                <p>We can run the same calculation here.</p>
                <p>In this case, that's <b>50% &times; {impactLeft} = <gold>{expectedImpactLeft}</gold></b>.</p>`,
        button1: {
            label: 'So how does that tell us the value of the idea?',
            lesson: '3.6',
        },
    },
    '3.6': {
        title: 'Combine',
        text: `<p>Now we just need to add our previous calculations together.</p>
                {calculation}
                <p>So the <i>Expected Revenue</i> at this gate is <blue>{expectedImpact}</blue>.</p>`,
        button1: {
            label: 'What about the costs?',
            lesson: '3.7',
        },
    },
    '3.7': {
        title: 'Costs',
        text: `<p>The <i>Expected Revenue</i> of the idea at the gate is <blue>{expectedImpact}</blue>, but the <i>Cost</i> of moving through this gate is <red>{cost}</red>.</p>
                <p>This means the <i>Expected Profit</i> of the idea at the gate is:</p>
                <p><b><blue>{expectedImpact}</blue> - <red>{cost}</red> = <purple>{expectedProfit}</purple></b></p>
                <p>If we open this gate, we should earn <purple>{expectedProfit}</purple>.</p>`,
        button1: {
            label: "So what's the <i>Expected {metricName}</i>?",
            lesson: '3.8',
        },
        annotation1: 'This is the cost for this gate.',
    },
    '3.8': {
        title: 'Expected {metricName}',
        text: `<p><i>{metricFullName}</i> is the ratio of <i>Profits</i> to <i>Costs</i>. (For a mission impact company, we'd use <i>Value</i> to <i>Costs</i>.) In this case, it's the <i>Expected Profit</i> of <purple>{expectedProfit}</purple> divided by <red>{cost}</red> of costs.</p>
                {calculation}
                <p>In other words, for every <red>{cost}</red> we spend, we should earn <purple>{expectedProfit}</purple>.</p>`,
        button1: {
            label: "But the outcome can't be {expectedMetricValue}!",
            lesson: '3.9',
        },
    },
    '3.9': {
        title: 'Expected vs. Actual',
        text: `<p>Of course, by playing one idea, we will never actually earn {expectedMetricValue} <i>{metricName}</i>.</p>
                <p>We will <i>either</i> return {profitRight} total ({impactRight} revenue - {cost} costs) and get {metricArticle} <i>{metricName}</i> of {metricRight} or lose {cost} ({impactLeft} revenue - {cost} costs) and get {metricArticle} <i>{metricName}</i> of {metricLeft}. But on average, we'll make <green>{expectedMetricValue}</green> per idea.</p>`,
        button1: {
            label: 'What? Prove it!',
            lesson: '3.10',
        },
        button2: {
            color: 'secondary',
            label: 'Got it - Show me the complete calculation',
            lesson: '3.13',
        },
    },
    '3.10': {
        title: 'Reversion to the Mean',
        text: `<p>As we start to play ideas, some succeed and some don't.</p>
                <p>The <i>{metricName}</i> swings up and down, but eventually, it stabilizes at <green>{expectedMetricValue}</green>. This is called <i>Reversion to the Mean</i> by statisticians.</p>
                <p>So while there is a lot of <i>variance</i>, the actual <i>{metricName}</i> will approach the <i>Expected {metricName}</i> over time.</p>`,
        button1: {
            label: "OK, let's move on",
            lesson: '3.11',
        },
    },
    '3.11': {
        title: 'Repeat as Needed',
        text: `<p>We can calculate the next gate the same way.</p>
                {calculation}`,
        button1: {
            action: 'nextGate',
            color: 'secondary',
            label: 'Show me one at a time',
        },
        button2: {
            label: 'Show me the whole row',
            lesson: '3.12',
        },
        button3: {
            color: 'secondary',
            label: 'Show me the whole board',
            lesson: '3.13',
        },
    },
    '3.12': {
        title: 'Keep Going',
        text: `<p>We can move up to the next row the same way, using the <i>Expected Revenue</i> of the row below.</p>
                {calculation}`,
        button1: {
            action: 'nextGate',
            color: 'secondary',
            label: 'Show me one at a time',
        },
        button2: {
            action: 'nextRow',
            color: 'secondary',
            label: 'Show me the whole row',
        },
        button3: {
            label: 'Show me the whole board',
            lesson: '3.13',
        },
    },
    '3.13': {
        title: 'Expected {metricName}',
        text: `<p>If we complete the calculation for the whole board, we can see that the overall <i>Expected {metricName}</i> is <green>{metricMaxDefault}</green>.</p>
                <p>From this point on, we'll show the <i>Expected {metricName}</i> for the whole board. If you hover your mouse over a specific gate, we'll also show the <i>Expected {metricName}</i> of that gate.</p>`,
        button1: {
            label: "Let's go back to my decisions",
            lesson: '3.14',
        },
    },
    '3.14': {
        title: 'Decision Criteria',
        text: `<p>If we look at your previous decisions on which gates to keep open, we can see that the <i>Expected {metricName}</i> is now <green>{expectedMetricValue}</green>.</p>
                <p>That's because closing gates stops some outcomes, and you also stop spending money.</p>
                <p><b>Use the <i>Expected {metricName}</i> meter when creating your decision criteria.</b></p>`,
        button1: {
            label: 'How does this work in the real world?',
            lesson: '3.15',
        },
        button2: {
            action: 'reset',
            color: 'secondary',
            label: 'Show me the {metricName} over time',
        },
    },
    '3.15': {
        title: 'The Real World',
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Globe',
        sound: 'lesson-learned',
        text: `<p>Calculations like this aren't so easy in the real world. But they can be done.</p>
                <p>Innovators can learn to make better estimations of possible outcomes and use statistics to calculate the <i>Expected {metricName}</i>.</p>
                <ul>
                    <li>Determine the possible outcomes.</li>
                    <li>Estimate the chance of each outcome.</li>
                    <li>Calculate the <i>Expected {metricName}</i>.</li>
                </ul>`,
        button1: {
            label: "Ok - let's get back to improving my decisions",
            lesson: '4',
        },
        annotation1:
            'Using a little math to set clear decision criteria can increase your <i>Expected {metricName}</i>.',
    },
    '4': {
        heading: 'Level 4',
        title: 'Decision Points',
        text: 'Increase {metricName} by choosing when to make decisions.',
        button: 'Play',
        image: '/assets/illustrations/signs-decision.png',
        imageAlt: 'Earlier and later signs pointing left and right',
    },
    '4.1': {
        title: 'Choose When to Decide',
        image: '/assets/illustrations/signs-decision.png',
        imageAlt: 'Earlier and later signs pointing left and right',
        text: `<p>Deciding earlier or later may help increase your <i>Expected {metricName}</i>.</p>
                <p><b>Move the decision point up or down by selecting a row.</b></p>`,
    },
    '4.1.1': {
        title: 'You Already Picked That',
        text: `<p>That was your original decision point.</p>
                <p><b>Move the decision point up or down by selecting a row.</b></p>`,
    },
    '4.1.2': {
        title: 'Earlier is Better',
        image: '/assets/illustrations/wheel-decision.png',
        imageAlt: 'Wheel of arrows pointing in different directions',
        text: `<p>Deciding earlier seems to increase the <i>Estimated {metricName}</i>, so take that to the extreme. Try deciding as early as possible.</p>
                <p><b>Move the decision point to the top of the board.</b></p>`,
    },
    '4.2': {
        title: 'Decide Later',
        image: '/assets/illustrations/later.png',
        imageAlt: 'Later sign pointing to the right',
        text: `<p>You've chosen to decide later. Let's see how that impacts your <i>Expected {metricName}</i>.</p>
                <p>Remember, your previous <i>Expected {metricName}</i> was {prevExpectedMetricValue}.</p>
                <p>We'll give you a budget of {budget} to test out this decision point.</p>
                <p><b>Kick off ten ideas.</b></p>`,
    },
    '4.2.1': {
        title: 'Evaluating Ideas Later',
        text: `<p>You've spent {spent} per idea in exchange for a lot of information about which direction the idea is going.</p>
                <p>At this point, there are fewer possibilities for the outcome of each idea. But it will only cost another {remaining} to see each idea get to market.</p>
                <p>Optimize your decision criteria by opening the gates that provide the best <i>Expected {metricName}</i>.</p>
                <p><b>Roll over each gate to see a hint.</b></p>`,
    },
    '4.2.2': {
        title: 'Oops!',
        image: '/assets/illustrations/bad-idea.png',
        imageAlt: 'Broken lightbulb',
        sound: 'negative',
        text: `<p>That wasn't the right gate to open. You can't improve your <i>Expected {metricName}</i>. In fact, you can only increase costs with that gate.</p>
                <p>With luck, you can win with a bad decision. But luck won't help you in the long run. Better to stop ideas that look likely to fail.</p>`,
        button1: {
            label: 'Reset the gates and let me try again',
            lesson: '4.2.1',
        },
    },
    '4.2.3': {
        title: 'Good Decision!',
        sound: 'positive',
        text: `<p>Just like before - even if it didn't pay out, it was the best decision you could make based on the data.</p>
                <p>You're only letting through ideas that improve your <i>Expected {metricName}</i>.</p>
                <p><b>Keep opening gates until you have the best decision criteria.</b></p>`,
    },
    '4.2.4': {
        title: 'Perfect Decisions, Bad Timing',
        image: '/assets/illustrations/wheel-decision.png',
        imageAlt: 'Wheel of arrows pointing in different directions',
        sound: 'negative',
        text: `<p>Your gate decisions were perfect, but you originally had a {prevExpectedMetricValue} <i>Expected {metricName}</i>. Now your <i>Expected {metricName}</i> is {expectedMetricValue} and your actual <i>{metricName}</i> is {metricValue}.</p>
                <p>You spent {spent} per idea for a lot of data to make great decisions. But at the decision point, you have limited impact.</p>
                <p>There is a tradeoff between more data and impactful decisions.</p>
                <p><b>Try making decisions earlier and compare the result.</b></p>`,
        button1: {
            label: 'Ok, let me change the timing',
            lesson: '4.1',
        },
    },
    '4.3': {
        title: 'Decide Earlier',
        image: '/assets/illustrations/earlier.png',
        imageAlt: 'Earlier sign pointing to the left',
        text: `<p>You've chosen to decide earlier. Let's see how that impacts the outcome.</p>
                <p>Remember, your previous <i>Expected {metricName}</i> was {prevExpectedMetricValue}.</p>
                <p>We'll give you a budget of {budget} to test out this decision point.</p>
                <p><b>Kick off ten ideas.</b></p>`,
    },
    '4.3.1': {
        title: 'Evaluating Ideas Early',
        text: `<p>You've spent {spent} per idea in exchange for information about which direction each idea is going.</p>
                <p>It will cost another {remaining} to see each idea get to market.</p>
                <p>Optimize your decision criteria by opening the gates that provide the best <i>Expected {metricName}</i>.</p>
                <p><b>Roll over each gate to see a hint.</b></p>`,
    },
    '4.3.2': {
        title: 'Yikes!',
        image: '/assets/illustrations/bad-idea.png',
        imageAlt: 'Broken lightbulb',
        sound: 'negative',
        text: `<p>That wasn't great. The odds of that gate delivering a positive outcome were not good.</p>
                <p>With luck, you can win with a bad decision. But in the long run being smart beats being lucky. Better to stop ideas that look likely to fail.</p>`,
        button1: {
            label: 'Restart and try again',
            lesson: '4.3.1',
        },
    },
    '4.3.3': {
        title: 'Good Decision!',
        sound: 'positive',
        text: `<p>Just like before - even if it didn't pay out, it was the best decision you could make based on the data.</p>
                <p>You're only letting through ideas that improve your outcome!</p>
                <p><b>Keep opening gates until you have the best decision criteria!</b></p>`,
    },
    '4.3.4': {
        title: 'Perfect Decisions!',
        image: '/assets/illustrations/wheel-decision.png',
        imageAlt: 'Wheel of arrows pointing in different directions',
        sound: 'negative',
        text: `<p>Remember, <i>Expected {metricName}</i> is more important that actual <i>{metricName}</i> in the long run.</p>
                <p>Making decisions earlier gives you a better <i>Expected {metricName}</i>. Your <i>Expected {metricName}</i> was {prevExpectedMetricValue} at first. By deciding earlier you've increased your <i>Expected {metricName}</i> to {expectedMetricValue}.</p>
                <p>But you can do even better.</p>
                <p><b>Move the decision point as early as possible.</b></p>`,
        button1: {
            label: 'Ok, let me try again',
            lesson: '4.1',
        },
    },
    '4.4': {
        title: 'Decide ASAP',
        image: '/assets/illustrations/earlier.png',
        imageAlt: 'Earlier sign pointing to the left',
        text: `<p>You've chosen to decide ASAP. Let's see how that impacts your <i>Expected {metricName}</i>.</p>
                <p>Remember, your previous <i>Expected {metricName}</i> was {prevExpectedMetricValue}.</p>
                <p>You have a budget of {budget} to test out this decision point.</p>
                <p><b>Kick off ten ideas.</b></p>`,
    },
    '4.4.1': {
        title: 'Evaluating Ideas ASAP',
        text: `<p>You've only spent {spent} per idea in exchange for a tiny bit of information about which direction each idea is going.</p>
                <p>It will cost another {remaining} to see each idea get to market.</p>
                <p>You've only got two gates to choose from. So open the one you think has the best chance of success to optimize your <i>Expected {metricName}</i>.</p>
                <p><b>Roll over each gate to see a hint.</b></p>`,
    },
    '4.4.2': {
        title: 'Nope!',
        image: '/assets/illustrations/bad-idea.png',
        imageAlt: 'Broken lightbulb',
        sound: 'negative',
        text: `<p>Sorry! There were only two gates and you picked the wrong one.</p>
                <p>Yes, you can win with a bad decision, but it won't improve your long term success.</p>
                <p>Even though you have very little information, from an investor's perspective it's better to stop early and invest in an idea heading in the right direction from the start.</p>`,
        button1: {
            label: 'Restart and try again',
            lesson: '4.4.1',
        },
    },
    '4.5': {
        title: 'Perfect!',
        image: '/assets/illustrations/victory.png',
        imageAlt: 'People celebrating',
        sound: 'positive',
        text: `<p>Making decisions as soon as possible gives you {expectedMetricValue} <i>Expected {metricName}</i>.</p>
                <p>Your actual <i>{metricName}</i> may be better or worse because there is still randomness. But your decision criteria are better.</p>
                <p>You only have a little information, but you have a disproportionate impact on your returns by eliminating ideas that will <u>never</u> be big winners.</p>`,
        button1: {
            label: 'How does this work in the real world?',
            lesson: '4.6',
        },
    },
    '4.6': {
        title: 'The Real World',
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Globe',
        sound: 'lesson-learned',
        text: `<p>There are plenty of ideas out there. Innovators with great track records:</p>
                <ul>
                    <li>Invest small amounts of time and money.</i>
                    <li>Stop ideas as early as possible to save money.</i>
                </ul>
                <p>All this requires the courage to <u>stop</u> projects.</p>
                <p>But in reality, you can make more than one decision...</p>`,
        button1: {
            label: 'How many decisions should I make?',
            lesson: '5',
        },
        annotation1: '<i>When</i> you make a decision is as important as the decision itself.',
    },
    '5': {
        heading: 'Level 5',
        title: 'Optimizing {metricName}',
        text: 'Create the best possible decision criteria by making multiple decisions.',
        button: 'Play',
        image: '/assets/illustrations/tools.png',
        imageAlt: 'Toolbox',
    },
    '5.1': {
        title: 'Optimize {metricName}',
        image: '/assets/illustrations/tools.png',
        imageAlt: 'Toolbox',
        text: `<p>You have total control now. Kick off ideas or open and close gates.</p>
                <p>Your goal is to exceed the target that the CEO and CFO have set by creating the best possible decision criteria for each step.</p>
                <p><b>Open and close gates until the <i>Expected {metricName}</i> is above {metricGoal}.</b></p>`,
        button1: {
            color: 'secondary',
            label: 'Mercy! I need a hint',
            lesson: '5.3',
        },
    },
    '5.2': {
        title: "Oops... you're bankrupt!",
        image: '/assets/illustrations/bankrupt.png',
        imageAlt: 'Financial loss graph',
        sound: 'negative',
        text: `<p>You've run out of cash!</p>
                <p>Just remember, you're trying to optimize the decision criteria by opening and closing gates. <u>Not</u> by kicking off ideas.</p>
                <p>Keep trying until you optimize the <i>Expected {metricName}</i> above {metricGoal}.</p>`,
        button1: {
            label: "Please restart and I'll try again",
            lesson: '5.1',
        },
    },
    '5.3': {
        title: 'Use the e{metricName} Meter',
        text: `<p>It's hard to calculate the probabilities in your head so look at the <i>Expected {metricName}</i>.</p>
                <p>This number will change based on which gates are open or closed.</p>
                <p>So just open and close gates and watch the meter go up and down.</p>
                <p><b>Open and close gates until the <i>Expected {metricName}</i> is above {metricGoal}.</b></p>`,
        button1: {
            color: 'secondary',
            label: 'Another hint please',
            lesson: '5.3.1',
        },
    },
    '5.3.1': {
        title: 'Keep It Simple',
        text: `<p>Don't overthink it.</p>
                <p>Start at the bottom left gate with the simple decisions.</p>
                <p>Based on the immediate rewards below - should you open or close that gate?</p>
                <p><b>Open and close gates until the <i>Expected {metricName}</i> is above {metricGoal}.</b></p>`,
        button1: {
            label: 'I surrender, this is too hard',
            lesson: '5.3.2',
        },
        button2: {
            action: 'next',
            color: 'secondary',
            label: 'Help me step by step',
        },
    },
    '5.3.2': {
        title: 'Surrender?',
        image: '/assets/illustrations/surrender.png',
        imageAlt: 'Hand waving a white flag',
        sound: 'negative',
        text: `<p>The AI that is coming for your job does not accept your surrender.</p>
                <p><b>Open and close gates until the <i>Expected {metricName}</i> is above {metricGoal}.</b></p>`,
        button1: {
            label: "Please restart and I'll try again",
            lesson: '5.1',
        },
        button2: {
            action: 'next',
            color: 'secondary',
            label: 'Help me step by step',
        },
    },
    '5.3.3': {
        title: 'Start Clean',
        image: '/assets/illustrations/cleaning-chalkboard.png',
        imageAlt: 'Hand wiping a chalkboard',
        text: `<p>Let's restart from a clean board.</p>`,
        button1: {
            label: 'Ok, please clear the gates',
            lesson: '5.3.4',
        },
    },
    '5.3.4': {
        title: 'Step by Step',
        image: '/assets/illustrations/earlier.png',
        imageAlt: 'Earlier sign pointing to the left',
        text: `<p>It's better to make decisions early, but try thinking backwards from the bottom up.</p>
                <p>Start at the bottom left gate and look at the potential rewards. Hover over to see the potential outcomes and <i>Expected {metricName}</i> for that gate.</p>
                <p>Would you want to keep this gate open or closed?</p>
                <p>Then go to the next gate in the row and follow the same process.</p>`,
    },
    '5.3.5': {
        title: 'Keep Going',
        text: `<p>Go gate by gate and row by row.</p>
                <p>Would you want to keep the gate open or closed?</p>
                <p>If you're not sure, just watch the <i>Expected {metricName}</i> as you open and close the gate.</p>
                <p><b>Open and close gates until the <i>Expected {metricName}</i> is above {metricGoal}.</b></p>`,
        button1: {
            color: 'secondary',
            label: "Can't you do this for me?",
            lesson: '5.3.6',
        },
    },
    '5.3.5.1': {
        title: 'Oops!',
        image: '/assets/illustrations/bad-idea.png',
        imageAlt: 'Broken lightbulb',
        text: `<p>That one didn't help you. It made your <i>Expected {metricName}</i> worse.</p>
                <p><b>Keep that gate open.</b></p>`,
    },
    '5.3.6': {
        title: 'No More Help',
        image: '/assets/illustrations/help.png',
        imageAlt: 'Submerged hand asking for rescue',
        text: `<p>Sorry, you just have to struggle through this.</p>
                <p>Just go gate by gate from the bottom to the top. Open and close each gate and watch the <i>Expected {metricName}</i>.</p>
                <p><b>Open and close gates until the <i>Expected {metricName}</i> is above {metricGoal}.</b></p>
                <p>If you're still not sure, you can review <i>Lesson 3: Predicting the Future.</i></p>`,
        button1: {
            color: 'secondary',
            label: 'Ok, take me back to <i>Predicting the Future</i>',
            lesson: '3',
        },
    },
    '5.4': {
        title: 'The CFO is thrilled!',
        image: '/assets/illustrations/cfo-happy.png',
        imageAlt: 'CFO with thumbs up',
        text: `<p>By setting decision criteria at each step, you've been able to improve your <i>Expected {metricName}</i> from {metricMaxDefault} all the way to {prevExpectedMetricValue}.</p>
                <p>But it's possible to get up to {metricMaxClosed} by choosing exactly the right gates.</p>
                <p>Keep Going!</p>
                <p><b>Open and close gates until the <i>Expected {metricName}</i> is {metricMaxClosed}.</b></p>`,
        button1: {
            action: 'next',
            color: 'secondary',
            label: "I'm stuck... how do I reach the maximum?",
        },
    },
    '5.4.1': {
        title: 'Local Maximum',
        image: '/assets/illustrations/road-sign.png',
        imageAlt: 'Left sign pointing left and red sign pointing right',
        text: `<p>Sometimes, you need to go backwards to go forwards.</p>
                <p>You closed a gate that may have improved your <i>Expected {metricName}</i> but is limiting your options further down the board. So it's stopping you from maximizing your decision criteria.</p>
                <p><b>Try opening this gate.</b></p>`,
    },
    '5.4.2': {
        title: 'Just Keep Trying',
        text: `<p>You've still got a few gates open that you should close to improve the <i>Expected {metricName}</i>.</p>
                <p>So try closing different gates.</p>
                <p><b>Open and close gates until the <i>Expected {metricName}</i> is above {metricMaxClosed}.</b></p>`,
        button1: {
            color: 'secondary',
            label: "Can't you just help me step by step?",
            lesson: '5.3.3',
        },
    },
    '5.5': {
        title: 'Victory!',
        image: '/assets/illustrations/victory.png',
        imageAlt: 'People celebrating',
        text: `<p>You've been able to improve your <i>Expected {metricName}</i> from {metricMaxDefault} all the way to {expectedMetricValue}.</p>
                <p>Making decisions early gives you the most impact, but continuing to make decisions as each project progresses can deliver even more improvements.</p>`,
        button1: {
            label: 'How does this work in the real world?',
            lesson: '5.6',
        },
    },
    '5.6': {
        title: 'The Real World',
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Globe',
        sound: 'lesson-learned',
        text: `<p>Making decisions can be expensive. But frequent, fast decisions can be cheap and efficient.</p> 
                <ul>
                    <li>Make frequent decisions.</li>
                    <li>Invest small amounts.</li>
                    <li>Stop projects as early as possible.</li>
                    <li>Reallocate money from stopped projects.</li>
                </ul>
                <p>All this requires the courage to <u>stop</u> projects.</p>
                <p>But costs are not evenly distributed...</p>`,
        button1: {
            label: 'What about costs?',
            lesson: '6',
        },
        annotation1: 'Frequent decisions can increase {metricName}.',
    },
    '6': {
        heading: 'Level 6',
        title: 'The Cost of Stages',
        text: 'Spending less time in the early stages of a project can lead to better outcomes.',
        button: 'Play',
        image: '/assets/illustrations/cash-register.png',
        imageAlt: 'A cash register',
    },
    '6.1': {
        title: 'Adjust Your Costs',
        image: '/assets/illustrations/cash-register.png',
        imageAlt: 'A cash register',
        text: `<p>Your costs aren't realistic right now.</p>
                <p>Some projects have a lot of research and development (R&D) costs up front and much less later. Other projects have a lot at the end.</p>
                <p>Let's see what happens when the costs shift.</p>
                <p><b>Move the costs up by clicking the orange triangle.</b></p>`,
    },
    '6.2': {
        title: 'Wrong Way',
        image: '/assets/illustrations/stop-sign.png',
        imageAlt: 'Stop sign',
        sound: 'negative',
        text: `<p>You are shifting your costs down. We'll go over that in a second.</p>
                <p><b>Move the costs up.</b></p>`,
    },
    '6.3': {
        title: 'Keep Going',
        text: `<p>Keep shifting the costs up as much as possible and watch the <i>Expected {metricName}</i> change.</p>
                <p><b>Move the costs up.</b></p>`,
        annotation1: 'Great! These costs are going up.',
    },
    '6.4': {
        title: 'Up Front Investment',
        text: `<p>You shifted your costs to the front of the project and your <i>Expected {metricName}</i> went down.</p>
                <p>That's because you know the least about your project at the start - so you are investing more while knowing less.</p>
                <p><b>Try moving costs down.</b></p>`,
    },
    '6.5': {
        title: 'Keep Going',
        text: `<p>Keep shifting the costs down as much as possible and watch the <i>Expected {metricName}</i> change.</p>
                <p><b>Move the costs down.</b></p>`,
    },
    '6.6': {
        title: 'Delayed Investment',
        text: `<p>You now shifted your investment to the end of the project and your <i>Expected {metricName}</i> went up considerably.</p>
                <p>The more you know before spending money, the better. It's better to delay major investments as late as possible.</p>
                <p>Some costs can't be delayed, but each project should focus on simple, fast, and cheap things that deliver data <u>first</u>.</p>`,
        button1: {
            label: 'Does this change the decision criteria?',
            lesson: '6.7',
        },
    },
    '6.7': {
        title: 'Update Decision Criteria',
        text: `<p>Since you've moved the costs, you might be able to optimize your decision criteria more.</p>
                <p>You should be able to get to {metricMaxClosedCostOffset}</p>
                <p><b>Open and close gates until the <i>Expected {metricName}</i> is above {metricMaxClosedCostOffset}.</b></p>`,
        button1: {
            action: 'next',
            color: 'secondary',
            label: 'I need a hint',
        },
    },
    '6.7.1': {
        title: 'Start Clean',
        image: '/assets/illustrations/cleaning-chalkboard.png',
        imageAlt: 'Hand wiping a chalkboard',
        text: `<p>Let's restart from a clean board.</p>`,
        button1: {
            label: 'Ok, please clear the gates',
            lesson: '6.7.2',
        },
    },
    '6.7.2': {
        title: 'Step by Step',
        image: '/assets/illustrations/earlier.png',
        imageAlt: 'Earlier sign pointing to the left',
        text: `<p>It's better to make decisions early, but try thinking backwards from the bottom up.</p>
                <p>Hover over the bottom left gate to see the gate's <i>Expected {metricName}</i>.</p>
                <p><b>Would you want to keep this gate open or closed?</b></p>
                <p>Then go to the next gate in the row and follow the same process.</p>`,
    },
    '6.7.3': {
        title: 'Keep Going',
        text: `<p>Go gate by gate and row by row.</p>
                <p>Would you want to keep this gate open or closed?</p>
                <p>If you're not sure, just watch the <i>Expected {metricName}</i> as you open and close the gate.</p>
                <p><b>Open and close gates until the <i>Expected {metricName}</i> is above {metricMaxClosedCostOffset}.</b></p>`,
        button1: {
            color: 'secondary',
            label: "Can't you do this for me?",
            lesson: '6.7.4',
        },
    },
    '6.7.3.1': {
        title: 'Oops!',
        image: '/assets/illustrations/bad-idea.png',
        imageAlt: 'Broken lightbulb',
        text: `<p>Closing that gate didn't help. It made your <i>Expected {metricName}</i> worse. (At least for now.)</p>
                <p>You can keep adjusting the gates <i>above</i> or <i>below</i> to reach {metricMaxClosedCostOffset} {metricName} <u>or</u> <b>open that gate.</b></p>`,
    },
    '6.7.4': {
        title: 'No More Help',
        image: '/assets/illustrations/help.png',
        imageAlt: 'Submerged hand asking for rescue',
        text: `<p>Sorry, you just have to struggle through this.</p>
                <p>Just go gate by gate from the bottom to the top. Open and close each gate and watch the <i>Expected {metricName}</i>.</p>
                <p><b>Open and close gates until the <i>Expected {metricName}</i> is above {metricMaxClosedCostOffset}.</b></p>
                <p>If you're still not sure, you can review <i>Lesson 3: Predicting the Future</i>.</p>`,
        button1: {
            color: 'secondary',
            label: 'Ok, take me back to <i>Predicting the Future</i>',
            lesson: '3',
        },
    },
    '6.7.5': {
        title: 'Double Check',
        image: '/assets/illustrations/road-sign.png',
        imageAlt: 'Left sign pointing left and red sign pointing right',
        text: `<p>Closing some gates has changed the <i>Expected {metricName}</i> at gates below it. So we need to double check the gates we left open.</p>
                <p>Let's start from the bottom again. Should you keep this gate open or closed?</p>
                <p><b>Keep going until the <i>Expected {metricName}</i> is above {metricMaxClosedCostOffset}.</b></p>`,
        button1: {
            color: 'secondary',
            label: 'Ok, take me back to <i>Predicting the Future</i>',
            lesson: '3',
        },
    },
    '6.8': {
        title: 'Maximum {metricName}',
        image: '/assets/illustrations/victory.png',
        imageAlt: 'People celebrating',
        text: `<p>Wow! {metricMaxClosedCostOffset} <i>Expected {metricName}</i>!</p>
                <p>Delaying your investment as much as possible allowed you to improve your decision criteria.</p>
                <p>Now you are stopping most of your projects early and only letting projects continue when they are sure to recover their initial investment.</p>`,
        button1: {
            label: 'How does this work in the real world?',
            lesson: '6.9',
        },
        annotation1: 'Stop most projects early.',
        annotation2: 'Let the sure things continue.',
    },
    '6.9': {
        title: 'The Real World',
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Globe',
        sound: 'lesson-learned',
        text: `<ul>
                    <li>Delaying investments until the last possible moment improves <i>{metricName}</i>.</li>
                    <li>Gather information as fast, cheap, and early as possible.</li>
                    <li>Adjust your decision criteria based on the new costs.</li>
                </ul>
                <p>In the real world, decisions themselves cost time and time is money. So too many decisions can be a bad thing if it increases costs.</p>
                <p>But there are other options than just stopping projects. We can allow a project to pivot.</p>`,
        button1: {
            label: 'What are pivots?',
            lesson: '7',
        },
        button2: {
            color: 'secondary',
            label: 'Let me adjust the costs manually',
            lesson: '6.10',
        },
        annotation1: 'Stop most projects early.',
        annotation2: 'Let the sure things continue.',
    },
    '6.10': {
        title: 'Extra Credit',
        image: '/assets/illustrations/money-bag.png',
        imageAlt: 'Bag of money',
        text: `<p>You can adjust the costs manually to try out different scenarios.</p>
                <p>For example, add an additional cost to each level to represent the cost of making decisions.</p>
                <p>When you are ready, learn more about pivots.</p>`,
        button1: {
            label: 'What are pivots?',
            lesson: '7',
        },
        annotation1: 'Click to adjust costs directly.',
    },
    '7': {
        heading: 'Level 7',
        title: 'Pivots',
        text: 'Increase {metricName} by allowing projects to repeat a stage and try again.',
        button: 'Play',
        image: '/assets/illustrations/pivot.png',
        imageAlt: 'Spiral arrow pointing right',
    },
    '7.1': {
        title: 'Pivots',
        image: '/assets/illustrations/pivot.png',
        imageAlt: 'Spiral arrow pointing right',
        text: `<p>In the real world, innovation projects can pivot. In other words, when something goes wrong, they fix it and try again.</p>
                <p>In this game, we represent pivots by bouncing off a gate for a second try.</p>
                <p><b>To create a bounce gate, click on the closed gate at the top.</b></p>`,
    },
    '7.2': {
        title: 'Second Chances',
        text: `<p>Creating a bounce gate allows projects that went the wrong way to try again.</p>
                <p><b>Kick off an idea.</b></p>`,
    },
    '7.3': {
        title: 'Déjà Vu',
        text: `<p>As you can see, we'll only let the ball bounce <u>once</u> and it's <u>not</u> guaranteed to work. You could wind up in the same spot!</p>
                <p><b>Kick off another idea.</b></p>`,
    },
    '7.4': {
        title: 'No impact?',
        image: '/assets/illustrations/ideas.png',
        imageAlt: 'Pile of lightbulbs',
        text: `<p>This time the idea didn't get stuck and you made it past the first row. But changing that gate into a bounce gate didn't help your <i>Expected {metricName}</i> at all.</p>
                <p>At this early stage, bouncing off the first gate doesn't have any impact because it's basically the same as starting a new project.</p>
                <p><b>Change the next gate down and watch the <i>Expected {metricName}</i>.</b></p>`,
    },
    '7.5': {
        title: 'Pivots Improve {metricName}',
        text: `<p>As you change more stage gates to bounce gates, you can see your <i>Expected {metricName}</i> improving.</p>
                <p>Instead of stopping the project entirely and reinvesting in a brand new idea from the top, you are investing <u>in the same project</u>.</p>
                <p>Pivots allow you to preserve the progress you've made.</p>
                <p><b>Change the next gate down and watch the <i>Expected {metricName}</i>.</b></p>`,
    },
    '7.6': {
        title: 'Keep Pivoting',
        text: `<p>It's almost always better to allow a pivot rather than stop a project.</p>
                <p>But depending on your cost and outcome structure, that's not always true.</p>
                <p>Test this for yourself.</p>
                <p><b>Change the next gate down and watch the <i>Expected {metricName}</i>.</b></p>`,
    },
    '7.7': {
        title: 'Can Pivoting Be Bad?',
        image: '/assets/illustrations/bad-pivot.png',
        imageAlt: 'Squiggly line pointing to the left',
        text: `<p>Huh - putting a bounce gate there makes things worse. Your <i>Expected {metricName}</i> went from {prevExpectedMetricValue} to {expectedMetricValue}.</p>
                <p>Sometimes when costs are higher relative to potential outcomes, a pivot isn't effective. Pivoting a project that has no chance of success is a waste of time.</p>
                <p>In this case, there are better places to pivot.</p>
                <p><b>Add more bounce gates until you reach an <i>Expected {metricName} of {metricMaxBounceCostOffset}.</b></p>`,
        button1: {
            action: 'next',
            color: 'secondary',
            label: 'I need a hint',
        },
    },
    '7.7.1': {
        title: 'Start Clean',
        image: '/assets/illustrations/cleaning-chalkboard.png',
        imageAlt: 'Hand wiping a chalkboard',
        text: `<p>Let's restart from a clean board.</p>`,
        button1: {
            label: 'Ok, please clear the gates',
            lesson: '7.7.2',
        },
    },
    '7.7.2': {
        title: 'Step by Step',
        text: `<p>With bounce gates, it's easier to start from the top and go gate by gate from right to left.</p>
                <p>If an idea is going in this direction, would you rather stop it or give it another chance?</p>
                <p><b>Change each gate from open to closed to a bounce gate and watch the <i>Expected {metricName}</i>.</b></p>`,
    },
    '7.7.3': {
        title: 'Keep Going',
        text: `<p>Go gate by gate and row by row.</p>
                <p>Would you want to keep this gate open, closed, or turn it into a bounce gate?</p>
                <p>If you're not sure, just watch the <i>Expected {metricName}</i> as you change each gate.</p>
                <p><b>Change each gate until you reach an <i>Expected {metricName}</i> of {metricMaxBounceCostOffset}.</b></p>`,
        button1: {
            color: 'secondary',
            label: "Can't you do this for me?",
            lesson: '7.7.4',
        },
    },
    '7.7.4': {
        title: 'No More Help',
        image: '/assets/illustrations/help.png',
        imageAlt: 'Submerged hand asking for rescue',
        text: `<p>Sorry, but you are almost finished with the whole game. You just have to struggle through this.</p>
                <p>Just go gate by gate from the top to the bottom. Change each gate and watch the <i>Expected {metricName}</i>.</p>
                <p><b>Change each gate until you reach an <i>Expected {metricName}</i> of {metricMaxBounceCostOffset}.</b></p>`,
    },
    '7.8': {
        title: 'Congratulations!',
        image: '/assets/illustrations/victory.png',
        imageAlt: 'People celebrating',
        text: `<p>You started with an <i>Expected {metricName}</i> of {metricMaxDefault}.</p>
                <p>Then you set decision criteria to get to {metricMaxClosed}.</p>
                <p>You changed your cost structure to reach {metricMaxClosedCostOffset}.</p>
                <p>Now by allowing pivots, you have an <i>Expected {metricName}</i> of {expectedMetricValue}!</p>`,
        button1: {
            label: 'How does this work in the real world?',
            lesson: '7.9',
        },
    },
    '7.9': {
        title: 'The Real World',
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Globe',
        sound: 'lesson-learned',
        text: `<ul>
                    <li>Pivots almost always improve results.</li>
                    <li>When costs become higher relative to outcomes, pivoting can reduce ROI.</li>
                    <li>Real projects can pivot more than once in the same stage.</li>
                </ul>
                <p>In real life, you sometimes need to stop projects because you run out of money or because the environment changes. But in principle, pivots are a good thing.</p>`,
        button1: {
            label: "What's next?",
            lesson: '7.10',
        },
        button2: {
            color: 'secondary',
            label: 'Understand the model? Read The Real Startup Book',
            link: RSB_URL,
        },
        annotation1:
            'Real-world pivots can be small changes such as selecting a new marketing channel or customer segment.',
    },
    '7.10': {
        title: 'Build your case with Krobar.ai',
        image: '/assets/illustrations/keep-learning.gif',
        imageAlt: 'Animated spinning globe with happy graduating student and some books',
        text: `<p>Found a stronger pivot path? Turn that insight into a stakeholder-ready business case.</p>
                <p>Krobar helps you model scenarios, compare risk, and present decision-ready forecasts in minutes.</p>`,
        button1: {
            analyticsName: 'krobar_ad_clicked',
            analyticsPayload: {
                resource_id: 'krobar_finish_cta',
                resource_type: 'ad',
                source: 'lesson_7_10',
            },
            label: 'Try Krobar.ai free',
            link: 'https://krobar.ai',
        },
    },
};

export const lessonKeys = Object.keys(lessons).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
);
export const lessonParents = Array.from(new Set(lessonKeys.map(key => parseInt(key))));
